<template>
  <div v-if="items && (items.length > 0 || alwaysShow)" :class="['analysis-card-compact', cardClass]">
    <div class="card-header-wrapper">
      <h4 class="card-header-compact">
        {{ title }}
      </h4>
      <i 
        v-if="editable"
        @click="toggleEdit" 
        :class="['edit-icon', isEditing ? 'las la-check' : 'las la-edit']" 
        :title="isEditing ? 'Save' : 'Edit'"
      ></i>
    </div>
    <ul v-if="!isEditing || !editable" class="analysis-list-compact">
      <li v-for="(item, index) in localItems" :key="index">{{ item }}</li>
    </ul>
    <div v-else-if="isEditing && editable" class="editable-list">
      <div v-for="(item, index) in localItems" :key="index" class="editable-item">
        <textarea
          v-model="localItems[index]"
          class="editable-textarea"
          rows="1"
          @input="onTextareaInput"
          @blur="onItemChange"
        ></textarea>
        <button @click="removeItem(index)" class="remove-item-btn" title="Remove item">
          <i class="las la-times"></i>
        </button>
      </div>
      <button @click="addItem" class="add-item-btn">
        <i class="las la-plus"></i>
        Add Item
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalysisCard',
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    cardClass: {
      type: String,
      default: ''
    },
    alwaysShow: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isEditing: false,
      localItems: []
    }
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.localItems = newItems ? [...newItems] : []
        if (this.isEditing) {
          this.resizeAllTextareas()
        }
      }
    }
  },
  methods: {
    resizeTextarea(textarea) {
      if (!textarea) return
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    },

    resizeAllTextareas() {
      this.$nextTick(() => {
        if (!this.$el) return
        const textareas = this.$el.querySelectorAll('.editable-textarea')
        textareas.forEach((textarea) => this.resizeTextarea(textarea))
      })
    },

    onTextareaInput(event) {
      this.resizeTextarea(event.target)
      this.onItemChange()
    },

    toggleEdit() {
      if (!this.editable) return;
      
      if (this.isEditing) {
        // Save changes
        this.$emit('update', this.localItems)
      }
      this.isEditing = !this.isEditing

      if (this.isEditing) {
        this.resizeAllTextareas()
      }
    },
    
    onItemChange() {
      // Emit update when item changes
      this.$emit('update', this.localItems)
    },
    
    addItem() {
      this.localItems.push('')
      this.$nextTick(() => {
        const textareas = this.$el.querySelectorAll('.editable-textarea')
        if (textareas.length > 0) {
          const lastTextarea = textareas[textareas.length - 1]
          this.resizeTextarea(lastTextarea)
          lastTextarea.focus()
        }
      })
    },
    
    removeItem(index) {
      this.localItems.splice(index, 1)
      this.$emit('update', this.localItems)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.analysis-card-compact {
  padding: 0;
}

.card-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
}

.card-header-compact {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-family: $brand-font-medium;
}

.edit-icon {
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  font-size: 1.3rem;
  padding: 0.25rem;
  border-radius: 4px;
  
  &:hover {
    background: #f8f9fa;
    color: $color-primary;
  }
}

.editable-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.editable-item {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.editable-textarea {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  font-family: $brand-font-regular;
  color: #495057;
  line-height: 1.5;
  resize: none;
  overflow: hidden;
  min-height: 2.5rem;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }
}

.remove-item-btn {
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 0.25rem;
  cursor: pointer;
  transition: color 0.15s ease;
  flex-shrink: 0;

  i {
    font-size: 1.3rem;
    color: #dc2626;
    transition: color 0.15s ease;
  }

  &:hover i {
    color: #ef4444;
  }
}

.add-item-btn {
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0;
  cursor: pointer;
  color: #0284c7;
  transition: color 0.15s ease;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  font-family: $brand-font-medium;

  &:hover {
    color: #0369a1;
  }

  i {
    font-size: 1rem;
    color: currentColor;
  }
}

.analysis-list-compact {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  li {
    padding-left: 1.25rem;
    color: #495057;
    line-height: 1.5;
    font-size: 1rem;
    font-family: $brand-font-regular;
    position: relative;
    
    &:before {
      content: '▸';
      position: absolute;
      left: 0;
      color: $color-primary;
      font-size: 1rem;
      font-weight: bold;
    }
  }
}
</style>

